import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { ExpensesContent } from './Expenses';
import { MastersContent } from './Masters';

const TABS = [
  { id: 'overview',   label: 'Overview',       icon: '🏬', desc: 'All store modules at a glance' },
  { id: 'expenses',   label: 'Expenses',        icon: '💸', desc: 'Track and manage all business expenses' },
  { id: 'masters',    label: 'Masters',         icon: '📂', desc: 'Units, brands and master data' },
  { id: 'stock_audit', label: 'Stock Audit',    icon: '📋', href: '/stock-audit',     desc: 'Physical stock verification and inventory reconciliation' },
  { id: 'delivery',   label: 'Delivery Notes',  icon: '🚚', href: '/delivery-notes',  desc: 'Delivery challans and dispatch tracking' },
  { id: 'accounting', label: 'Accounting',      icon: '📒', href: '/accounting',      desc: 'Ledgers, journals and financial controls' },
  { id: 'warehouse',  label: 'Warehouse',       icon: '🏭', href: '/warehouse',       desc: 'Stock transfers, movements and warehouse flow' },
  { id: 'crm',        label: 'CRM',             icon: '🎯', href: '/crm',             desc: 'Leads, follow-ups and customer lifecycle' },
  { id: 'hr',         label: 'HR',              icon: '👔', href: '/hr',              desc: 'Employees, attendance and payroll' },
  { id: 'branches',   label: 'Branches',        icon: '🏢', href: '/branches',        desc: 'Multi-branch controls and collections' },
  { id: 'amc',        label: 'AMC',             icon: '📅', href: '/amc',             desc: 'Service contracts, renewals and support' },
];

const MODULE_GROUPS = [
  {
    group: 'Business Operations',
    color: '#3B82F6',
    modules: ['expenses', 'delivery', 'accounting'],
  },
  {
    group: 'Inventory & Data',
    color: '#10B981',
    modules: ['masters', 'warehouse', 'stock_audit'],
  },
  {
    group: 'People & Growth',
    color: '#8B5CF6',
    modules: ['crm', 'hr', 'branches', 'amc'],
  },
];

const StoreManagement = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { darkMode, color } = useTheme();
  const acc = color.swatch;

  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const valid = TABS.some(t => t.id === tabParam);
    setActiveTab(valid ? tabParam : 'overview');
  }, [searchParams]);

  const setTab = (tabId) => {
    setSearchParams({ tab: tabId }, { replace: true });
  };

  const bg       = darkMode ? '#0F172A' : '#F1F5F9';   // eslint-disable-line no-unused-vars
  const cardBg   = darkMode ? '#1E293B' : '#FFFFFF';
  const border   = darkMode ? '#334155' : '#E2E8F0';
  const pg       = useMemo(() => ({ color: darkMode ? '#F1F5F9' : '#0F172A' }), [darkMode]);
  const muted    = useMemo(() => ({ color: darkMode ? '#64748B' : '#94A3B8' }), [darkMode]);

  const tabById  = (id) => TABS.find(t => t.id === id);
  const selected = tabById(activeTab) || TABS[0];

  const handleModuleClick = (tab) => {
    if (tab.href) navigate(tab.href);
    else setTab(tab.id);
  };

  return (
    <Layout>
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 4px' }}>

        {/* ── Hero header ── */}
        <div style={{
          borderRadius: 18,
          background: `linear-gradient(135deg, ${acc} 0%, ${acc}bb 60%, ${darkMode ? '#0F172A' : '#EFF6FF'} 100%)`,
          padding: '28px 32px',
          marginBottom: 24,
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: -50, right: 60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <span style={{ fontSize: 30 }}>🏬</span>
                <h1 style={{ color: '#fff', fontSize: 26, fontWeight: 900, margin: 0, letterSpacing: '-0.5px' }}>Store Management</h1>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, margin: 0, maxWidth: 480 }}>
                Your business command center — expenses, masters, accounting, warehouse, HR, CRM and more in one place.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={() => setTab('expenses')}
                style={{ padding: '9px 18px', background: 'rgba(255,255,255,0.2)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', backdropFilter: 'blur(8px)' }}>
                💸 Add Expense
              </button>
              <button onClick={() => setTab('masters')}
                style={{ padding: '9px 18px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', backdropFilter: 'blur(8px)' }}>
                📂 Masters
              </button>
            </div>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div style={{
          display: 'flex', gap: 4, marginBottom: 24, flexWrap: 'wrap',
          background: cardBg, borderRadius: 14, padding: 6,
          border: `1px solid ${border}`,
          boxShadow: darkMode ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
        }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => tab.href ? navigate(tab.href) : setTab(tab.id)}
              style={{
                padding: '8px 14px', border: 'none', borderRadius: 10,
                fontWeight: 700, fontSize: 12, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 5,
                background: activeTab === tab.id ? acc : 'transparent',
                color: activeTab === tab.id ? '#fff' : (darkMode ? '#94A3B8' : '#64748B'),
                transition: 'all 0.18s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { if (activeTab !== tab.id) e.currentTarget.style.background = darkMode ? '#334155' : '#F1F5F9'; }}
              onMouseLeave={e => { if (activeTab !== tab.id) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontSize: 14 }}>{tab.icon}</span>
              {tab.label}
              {tab.href && <span style={{ fontSize: 9, opacity: 0.6 }}>↗</span>}
            </button>
          ))}
        </div>

        {/* ══ OVERVIEW TAB ══ */}
        {activeTab === 'overview' && (
          <div>
            {MODULE_GROUPS.map(group => (
              <div key={group.group} style={{ marginBottom: 28 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 4, height: 20, borderRadius: 2, background: group.color }} />
                  <span style={{ ...pg, fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{group.group}</span>
                  <div style={{ flex: 1, height: 1, background: border }} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
                  {group.modules.map(modId => {
                    const tab = tabById(modId);
                    if (!tab) return null;
                    const isExternal = !!tab.href;
                    return (
                      <div
                        key={modId}
                        style={{
                          background: cardBg, borderRadius: 14,
                          border: `1px solid ${border}`,
                          padding: '18px 20px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          display: 'flex', flexDirection: 'column', gap: 10,
                          boxShadow: darkMode ? 'none' : '0 1px 4px rgba(0,0,0,0.05)',
                          position: 'relative', overflow: 'hidden',
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.borderColor = group.color;
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = `0 8px 24px ${group.color}22`;
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.borderColor = border;
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = darkMode ? 'none' : '0 1px 4px rgba(0,0,0,0.05)';
                        }}
                        onClick={() => handleModuleClick(tab)}
                      >
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: group.color, borderRadius: '14px 14px 0 0', opacity: 0.7 }} />

                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                          <div style={{ width: 42, height: 42, borderRadius: 12, background: `${group.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                            {tab.icon}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ ...pg, fontWeight: 800, fontSize: 14 }}>{tab.label}</div>
                            <div style={{ color: group.color, fontSize: 10, fontWeight: 700, marginTop: 1 }}>
                              {isExternal ? '↗ Full Screen Module' : '⚡ Embedded in this page'}
                            </div>
                          </div>
                        </div>

                        <p style={{ ...muted, fontSize: 12, margin: 0, lineHeight: 1.5 }}>{tab.desc}</p>

                        <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                          <button
                            onClick={e => { e.stopPropagation(); handleModuleClick(tab); }}
                            style={{ flex: 1, padding: '7px 10px', background: group.color, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
                          >
                            {isExternal ? `Open ${tab.label}` : `Go to ${tab.label}`}
                          </button>
                          {!isExternal && (
                            <button
                              onClick={e => { e.stopPropagation(); setTab(tab.id); }}
                              style={{ padding: '7px 12px', background: 'transparent', color: group.color, border: `1px solid ${group.color}44`, borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                            >
                              Open Tab
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <div style={{
              background: darkMode ? '#1E293B' : '#EFF6FF',
              border: `1px solid ${darkMode ? '#334155' : '#BFDBFE'}`,
              borderRadius: 12, padding: '14px 18px',
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 20 }}>💡</span>
              <div>
                <div style={{ ...pg, fontWeight: 700, fontSize: 13 }}>Quick Tips</div>
                <div style={{ ...muted, fontSize: 12 }}>
                  Use <kbd style={{ fontSize: 10, padding: '1px 5px', borderRadius: 4, background: darkMode ? '#0F172A' : '#F1F5F9', border: `1px solid ${border}` }}>Alt+M</kbd> to jump here from anywhere.
                  &nbsp;Expenses &amp; Masters are fully embedded — no page switch needed.
                  &nbsp;Accounting, Warehouse, HR, CRM, Branches &amp; AMC open in their own dedicated screens.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══ EXPENSES TAB ══ */}
        {activeTab === 'expenses' && (
          <div style={{ background: cardBg, borderRadius: 16, border: `1px solid ${border}`, boxShadow: darkMode ? 'none' : '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, background: darkMode ? '#0F172A' : '#FAFBFC' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: `${acc}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>💸</div>
                <div>
                  <div style={{ ...pg, fontWeight: 800, fontSize: 15 }}>Expenses</div>
                  <div style={{ ...muted, fontSize: 11 }}>Track and manage all your business expenses</div>
                </div>
              </div>
              <button onClick={() => setTab('overview')}
                style={{ padding: '6px 14px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer', ...muted }}>
                ← Back to Overview
              </button>
            </div>
            <ExpensesContent />
          </div>
        )}

        {/* ══ MASTERS TAB ══ */}
        {activeTab === 'masters' && (
          <div style={{ background: cardBg, borderRadius: 16, border: `1px solid ${border}`, boxShadow: darkMode ? 'none' : '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, background: darkMode ? '#0F172A' : '#FAFBFC' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: '#10B98118', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📂</div>
                <div>
                  <div style={{ ...pg, fontWeight: 800, fontSize: 15 }}>Masters</div>
                  <div style={{ ...muted, fontSize: 11 }}>Manage units of measurement, brands and other master data</div>
                </div>
              </div>
              <button onClick={() => setTab('overview')}
                style={{ padding: '6px 14px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: 'pointer', ...muted }}>
                ← Back to Overview
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <MastersContent />
            </div>
          </div>
        )}

        {/* ══ EXTERNAL MODULE TABS ══ */}
        {!['overview', 'expenses', 'masters'].includes(activeTab) && selected.href && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 340 }}>
            <div style={{ background: cardBg, borderRadius: 20, border: `1px solid ${border}`, padding: '40px 48px', textAlign: 'center', boxShadow: darkMode ? 'none' : '0 4px 24px rgba(0,0,0,0.08)', maxWidth: 440, width: '100%' }}>
              <div style={{ width: 72, height: 72, borderRadius: 20, background: `${acc}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 34, margin: '0 auto 16px' }}>
                {selected.icon}
              </div>
              <h2 style={{ ...pg, fontSize: 20, fontWeight: 900, margin: '0 0 8px' }}>{selected.label}</h2>
              <p style={{ ...muted, fontSize: 13, margin: '0 0 28px', lineHeight: 1.6 }}>{selected.desc}</p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => navigate(selected.href)}
                  style={{ padding: '11px 28px', background: acc, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 14, cursor: 'pointer', boxShadow: `0 4px 14px ${acc}44` }}>
                  Open {selected.label} →
                </button>
                <button onClick={() => setTab('overview')}
                  style={{ padding: '11px 20px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer', ...muted }}>
                  ← Overview
                </button>
              </div>
              <div style={{ marginTop: 20, padding: '10px 14px', background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 8, border: `1px solid ${border}` }}>
                <div style={{ ...muted, fontSize: 11 }}>This module opens in its own full-screen view for the best experience.</div>
              </div>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default StoreManagement;
